/**
 * Validation utilities for enhanced shipment costing data model
 */

import { COSTING_VALIDATION_RULES, SAUDI_VAT_RATE } from '../types/shipment-costing-types';
import { 
  calculateFromInclusive, 
  calculateFromExclusive, 
  validateAmount,
  validateCostingVAT,
  validateFormSubmission 
} from './vat-calculations';
import { 
  handleValidationErrors, 
  categorizeError, 
  logError 
} from './vat-error-handling';

/**
 * Validates a costing data object with comprehensive error handling
 * @param {Object} costingData - The costing data to validate
 * @param {Object} options - Validation options
 * @returns {Object} Validation result with detailed error information
 */
export const validateCostingData = (costingData, options = {}) => {
  const { strictMode = false, includeWarnings = true } = options;
  const errors = [];
  const warnings = [];
  
  try {
    // Use the enhanced VAT validation
    const vatValidation = validateCostingVAT(costingData);
    errors.push(...vatValidation.errors);
    if (includeWarnings) {
      warnings.push(...vatValidation.warnings);
    }
    
    // Validate stops with enhanced error handling
    if (costingData.stops && Array.isArray(costingData.stops)) {
      costingData.stops.forEach((stop, index) => {
        try {
          if (!stop.description || stop.description.trim() === '') {
            errors.push(`Stop ${index + 1}: Description is required`);
          }
          
          const amountValidation = validateAmount(stop.amount, {
            fieldName: `Stop ${index + 1} amount`,
            allowZero: false,
            maxAmount: COSTING_VALIDATION_RULES.stopAmount?.max || 50000
          });
          
          if (!amountValidation.isValid) {
            errors.push(amountValidation.error);
          } else if (includeWarnings) {
            warnings.push(...amountValidation.warnings);
          }
          
          const quantityValidation = validateAmount(stop.quantity, {
            fieldName: `Stop ${index + 1} quantity`,
            allowZero: false,
            maxAmount: 1000
          });
          
          if (!quantityValidation.isValid) {
            errors.push(quantityValidation.error);
          }
          
        } catch (error) {
          const categorized = categorizeError(error, { operation: 'stop_validation', index });
          errors.push(`Stop ${index + 1}: ${categorized.userMessage}`);
          logError(categorized, { stopIndex: index });
        }
      });
    }
    
    // Validate other costs with enhanced error handling
    if (costingData.otherCosts && Array.isArray(costingData.otherCosts)) {
      costingData.otherCosts.forEach((cost, index) => {
        try {
          if (!cost.description || cost.description.trim() === '') {
            errors.push(`Other cost ${index + 1}: Description is required`);
          }
          
          const amountValidation = validateAmount(cost.amount, {
            fieldName: `Other cost ${index + 1} amount`,
            allowZero: false,
            maxAmount: COSTING_VALIDATION_RULES.otherCostAmount?.max || 50000
          });
          
          if (!amountValidation.isValid) {
            errors.push(amountValidation.error);
          } else if (includeWarnings) {
            warnings.push(...amountValidation.warnings);
          }
          
        } catch (error) {
          const categorized = categorizeError(error, { operation: 'other_cost_validation', index });
          errors.push(`Other cost ${index + 1}: ${categorized.userMessage}`);
          logError(categorized, { costIndex: index });
        }
      });
    }
    
    // Additional validation in strict mode
    if (strictMode) {
      // Validate required fields
      if (!costingData.shipmentId) {
        errors.push('Shipment ID is required');
      }
      
      if (!costingData.companyId) {
        errors.push('Company ID is required');
      }
      
      // Check for at least one cost entry
      const hasCosts = (
        (costingData.fuelAmount && parseFloat(costingData.fuelAmount) > 0) ||
        (costingData.maintenanceAmount && parseFloat(costingData.maintenanceAmount) > 0) ||
        (costingData.commissionAmount && parseFloat(costingData.commissionAmount) > 0) ||
        (costingData.stops && costingData.stops.length > 0) ||
        (costingData.otherCosts && costingData.otherCosts.length > 0)
      );
      
      if (!hasCosts) {
        warnings.push('No cost entries found. At least one cost should be entered.');
      }
    }
    
    // Process errors with error handling utility
    const processedErrors = handleValidationErrors(errors, { formType: 'costing_data' });
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      processedErrors,
      canSubmit: processedErrors.canSubmit,
      summary: {
        totalErrors: errors.length,
        totalWarnings: warnings.length,
        criticalErrors: processedErrors.criticalErrors?.length || 0
      }
    };
    
  } catch (error) {
    const categorized = categorizeError(error, { operation: 'costing_data_validation' });
    logError(categorized, { costingData });
    
    return {
      isValid: false,
      errors: [categorized.userMessage],
      warnings: [],
      processedErrors: handleValidationErrors([categorized.userMessage]),
      canSubmit: false,
      summary: {
        totalErrors: 1,
        totalWarnings: 0,
        criticalErrors: 1
      }
    };
  }
};

/**
 * Validates VAT calculation accuracy
 * @param {number} enteredAmount - Amount entered by user
 * @param {number} baseAmount - Calculated base amount
 * @param {number} vatAmount - Calculated VAT amount
 * @param {boolean} includesVAT - Whether entered amount includes VAT
 * @returns {Object} Validation result
 */
export const validateVATCalculation = (enteredAmount, baseAmount, vatAmount, includesVAT) => {
  const tolerance = COSTING_VALIDATION_RULES.vatCalculation.tolerance;
  
  try {
    // Calculate expected values
    const expected = includesVAT 
      ? calculateFromInclusive(enteredAmount)
      : calculateFromExclusive(enteredAmount);
    
    // Check if calculated values match expected values within tolerance
    const baseAmountValid = Math.abs(baseAmount - expected.baseAmount) <= tolerance;
    const vatAmountValid = Math.abs(vatAmount - expected.vatAmount) <= tolerance;
    const totalValid = includesVAT 
      ? Math.abs(enteredAmount - expected.total) <= tolerance
      : Math.abs((baseAmount + vatAmount) - expected.total) <= tolerance;
    
    if (!baseAmountValid) {
      return { isValid: false, error: 'Base amount calculation is incorrect' };
    }
    
    if (!vatAmountValid) {
      return { isValid: false, error: 'VAT amount calculation is incorrect' };
    }
    
    if (!totalValid) {
      return { isValid: false, error: 'Total amount calculation is incorrect' };
    }
    
    return { isValid: true };
    
  } catch (error) {
    return { isValid: false, error: `Calculation error: ${error.message}` };
  }
};

/**
 * Validates that required fields are present for a given costing status
 * @param {Object} costingData - The costing data to validate
 * @param {string} status - The target status
 * @returns {Object} Validation result
 */
export const validateCostingStatus = (costingData, status) => {
  const errors = [];
  
  switch (status) {
    case 'completed':
      // For completed status, at least one cost type should be present
      const hasFuel = costingData.fuelAmount > 0;
      const hasMaintenance = costingData.maintenanceAmount > 0;
      const hasCommission = costingData.commissionAmount > 0;
      const hasStops = costingData.stops && costingData.stops.length > 0;
      const hasOtherCosts = costingData.otherCosts && costingData.otherCosts.length > 0;
      
      if (!hasFuel && !hasMaintenance && !hasCommission && !hasStops && !hasOtherCosts) {
        errors.push('At least one cost entry is required for completed status');
      }
      break;
      
    case 'partial':
      // Partial status is valid as long as some data is entered
      break;
      
    case 'not_added':
      // Not added status is always valid
      break;
      
    default:
      errors.push(`Invalid costing status: ${status}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Sanitizes costing data by ensuring proper types and removing invalid values
 * @param {Object} costingData - Raw costing data
 * @returns {Object} Sanitized costing data
 */
export const sanitizeCostingData = (costingData) => {
  const sanitized = { ...costingData };
  
  // Back-compat: map legacy keys to canonical *Amount
  if (sanitized.fuelAmount == null && sanitized.fuel != null) sanitized.fuelAmount = sanitized.fuel;
  if (sanitized.maintenanceAmount == null && sanitized.maintenance != null) sanitized.maintenanceAmount = sanitized.maintenance;
  if (sanitized.commissionAmount == null && sanitized.commission != null) sanitized.commissionAmount = sanitized.commission;
  
  // Sanitize numeric fields
  const numericFields = [
    'fuelAmount', 'fuelBaseAmount', 'fuelVATAmount',
    'maintenanceAmount', 'maintenanceBaseAmount', 'maintenanceVATAmount',
    'commissionAmount',
    // New separated fuel cost fields
    'petroAppFuelAmount', 'petroAppFuelBaseAmount', 'petroAppFuelVATAmount',
    'manualFuelAmount', 'manualFuelBaseAmount', 'manualFuelVATAmount',
    'totalFuelAmount', 'totalFuelBaseAmount', 'totalFuelVATAmount'
  ];
  
  numericFields.forEach(field => {
    if (sanitized[field] !== undefined && sanitized[field] !== null) {
      const value = parseFloat(sanitized[field]);
      sanitized[field] = isNaN(value) ? 0 : Math.max(0, value);
    } else {
      sanitized[field] = 0;
    }
  });
  
  // Sanitize boolean fields
  const booleanFields = [
    'fuelIncludesVAT', 'maintenanceIncludesVAT',
    // New separated fuel cost boolean fields
    'petroAppFuelIncludesVAT', 'manualFuelIncludesVAT'
  ];
  booleanFields.forEach(field => {
    if (sanitized[field] !== undefined && sanitized[field] !== null) {
      sanitized[field] = Boolean(sanitized[field]);
    } else {
      sanitized[field] = true; // Default to VAT-inclusive
    }
  });
  
  // Sanitize arrays
  if (!sanitized.stops || !Array.isArray(sanitized.stops)) {
    sanitized.stops = [];
  }
  
  if (!sanitized.otherCosts || !Array.isArray(sanitized.otherCosts)) {
    sanitized.otherCosts = [];
  }
  
  // Sanitize applied fuel transactions array
  if (!sanitized.appliedFuelTransactions || !Array.isArray(sanitized.appliedFuelTransactions)) {
    sanitized.appliedFuelTransactions = [];
  }
  
  // Sanitize strings
  if (!sanitized.notes || typeof sanitized.notes !== 'string') {
    sanitized.notes = '';
  }
  
  return sanitized;
};